/**
 * Shared geometry and formatting for the operations panel charts. They are drawn by hand in SVG
 * rather than through a chart library: every one of them answers a different question and needed a
 * shape of its own - a band between two percentiles, a diverging bar, a strip that shares the x
 * axis of the plot above it.
 */

// Rounds an axis maximum up to a readable number so the gridlines land on values people recognise.
export const niceMax = value => {
  if (!value || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const steps = [1.5, 2, 3, 4, 5, 8, 10];
  const step = steps.find(candidate => normalized <= candidate) ?? 10;
  return step * magnitude;
};

export const linePath = points =>
  points
    .map(
      ([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`
    )
    .join(' ');

// An area between two lines: forward along the upper one, back along the lower one.
export const bandPath = (upper, lower) =>
  `${linePath(upper)} ${linePath([...lower].reverse()).replace('M', 'L')} Z`;

export const gridValues = (max, steps = 4) =>
  Array.from({ length: steps + 1 }, (_, index) => (max / steps) * index);

export const localeTag = locale => String(locale).replace('_', '-');

export const formatCount = (value, locale) =>
  Number(value || 0).toLocaleString(localeTag(locale));

export const formatDay = (timestamp, locale, options) =>
  new Date(timestamp * 1000).toLocaleDateString(localeTag(locale), options);

export const percentOf = (value, total) => (total ? (value / total) * 100 : 0);

export const formatPercent = (value, locale, digits = 1) =>
  Number(value || 0).toLocaleString(localeTag(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

// Enough labels to read the axis, never so many they collide - a week gets one per day, a quarter
// one per fortnight.
export const axisStride = length => Math.max(1, Math.ceil(length / 6));
